/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { ConnectOptions } from 'twilio-video';
import { useLocalTracks } from './hooks/useLocalTracks';
import { useVideoRoom } from './hooks/useVideoRoom';
import { SelectedParticipantProvider } from './selectedParticipantContext';
import { videoProviderFactory } from './videoProviderFactory';
import { VideoProviderName, VideoProviderType } from './types';
import { useClosedCaptioningSupport } from './hooks/useClosedCaptioningSupport';
import AttachVisibilityHandler from '@components/videoProvider/twilio/AttachVisibilityHandler/AttachVisibilityHandler';

export type VideoContext = ReturnType<typeof useLocalTracks> &
  ReturnType<typeof useVideoRoom> & { videoProvider: VideoProviderType } & ReturnType<
    typeof useClosedCaptioningSupport
  >;
export const VideoContext = createContext<VideoContext | null>(null);

export function useVideoContext(): VideoContext {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('called useVideoContext without a context, must nest component under a VideoProvider');
  }
  return context;
}

export interface VideoProviderProps {
  opts?: ConnectOptions;
  connectWithTracks?: boolean;
  videoProviderName?: VideoProviderName;
}
export const VideoProvider: React.FC<VideoProviderProps> = ({
  children,
  opts,
  connectWithTracks,
  videoProviderName,
}) => {
  const defaultProviderType = videoProviderName || VideoProviderName.TWILIO;
  const [videoProvider, setVideoProvider] = useState<VideoProviderType>(videoProviderFactory(defaultProviderType));

  const {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
    isFetchingLocalTracks,
  } = useLocalTracks(videoProvider);
  const { room, connect, isConnecting, isVideoConnectionBroken } = useVideoRoom(
    videoProvider,
    localTracks,
    opts,
    connectWithTracks,
  );
  const { closedCaptions, toggleClosedCaptioning } = useClosedCaptioningSupport(videoProvider);

  const props = {
    room,
    connect,
    isConnecting,
    isVideoConnectionBroken,
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
    isFetchingLocalTracks,
    videoProvider,
    closedCaptions,
    toggleClosedCaptioning,
  };

  useEffect(() => {
    setVideoProvider(videoProviderFactory(defaultProviderType));
  }, [defaultProviderType]);

  return (
    <VideoContext.Provider value={props}>
      <SelectedParticipantProvider room={room}>{children}</SelectedParticipantProvider>
      {/*
        The AttachVisibilityHandler component is using the useLocalVideoToggle hook
        which must be used within the VideoContext Provider.
      */}
      <AttachVisibilityHandler />
    </VideoContext.Provider>
  );
};
